import { CREDENTIAL_PROMPTS_AVAILABLE } from '~/constants/features'
import type { useUserStore } from '~/stores/user'

/**
 * Whether this user is one who can act on a credential prompt: allowed to edit
 * connections, and in an org type that has the credentials page. Shared by the
 * onboarding dialog and the credential gap prompt, which ask for the same thing,
 * and by the triggers, so they don't fetch statuses for users who can't see one.
 *
 * False until `userInfo` has loaded: every one of these flags reads a permissive
 * default while it is in flight.
 */
export function canBeAskedForCredentials(
  user: Pick<
    ReturnType<typeof useUserStore>,
    | 'sessionReady'
    | 'canEditConnections'
    | 'isSubCarrier'
    | 'isUTASOnlyOrg'
    | 'isTerminalOrg'
  >
): boolean {
  return (
    user.sessionReady &&
    user.canEditConnections &&
    !user.isSubCarrier &&
    !user.isUTASOnlyOrg &&
    !user.isTerminalOrg
  )
}

/**
 * Whether it is worth reading terminal statuses to decide on a prompt right now:
 * the build has the prompts and the user can act on one. Triggers check this
 * first, so a user who could never see the prompt costs no requests.
 */
export function shouldTriggerCredentialPrompt(
  user: Parameters<typeof canBeAskedForCredentials>[0]
): boolean {
  return CREDENTIAL_PROMPTS_AVAILABLE && canBeAskedForCredentials(user)
}
